import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CardBody } from '../../components/CardBody';
import { CardFooter } from '../../components/CardFooter';
import { CardHeader } from '../../components/CardHeader';
import { Color } from '../../enums/Color.enum';
import { ViewProduct } from '../../models/ViewProduct.model';
import { getPopularity } from '../../product_detail/components/ProductRelatedItems';
import { LongSize } from '../../product_detail/components/size.enum';
import { activeNavCategorySetState, appliedCollectionFilterState, collectionSelectedVariantFamily } from '../../recoil/atoms';
import { categoryFilterProductsState, isMobileState } from '../../recoil/selectors';
import { GarmentSort } from './CollectionFilters';
import { CollectionHeader } from './CollectionHeader';
import { CollectionCard } from './CollectionCard';
import { allCollections } from '../../hooks/actions/useNavCategories';
import { Collection } from '../../models/Collection.model';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { CollectionHoverHeader } from './CollectionHoverHeader';
import styles from './CollectionCards.module.scss';
import { ProductFeature } from '../../enums/ProductFeature.enum';
import { bundleConfiguration } from '../../hooks/bundleConfiguration';
import { ProductTag } from '../../enums/ProductTag.enum';
import CollectionBundleCard from './CollectionBundleCard';
import { BundleCollectionHandle } from '../../enums/CollectionHandle.enum';

interface Props {
  hideHeader?: boolean;
}

export const CollectionCards: React.FC<Props> = ({ hideHeader = false }) => {
  const isMobile = useRecoilValue(isMobileState);
  const availableProducts = useRecoilValue(categoryFilterProductsState);
  const appliedFilters = useRecoilValue(appliedCollectionFilterState);
  const activeNavCategorySet = useRecoilValue(activeNavCategorySetState);
  const [hoverProductHandle, setHoverProductHandle] = useState<ProductHandle | undefined>();
  const potentialHoverVariant = useRecoilValue(collectionSelectedVariantFamily(hoverProductHandle ?? ProductHandle.SuperPowerShort));
  const hoverVariant = hoverProductHandle && potentialHoverVariant;
  const hoverProduct = hoverProductHandle && availableProducts.find((p) => p.handle === hoverProductHandle);

  const filteredProducts = availableProducts
    .filter(filterSize(appliedFilters.size))
    .filter(filterColor(appliedFilters.color))
    .filter(filterFeature(appliedFilters.feature))
    .sort(sortProducts(appliedFilters.sort[0]));

  let debounceTimeout: ReturnType<typeof setTimeout>;
  const setHoverProductHandleDebounce = (productHandle: ProductHandle | undefined) => {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => setHoverProductHandle(productHandle), 200);
  };
  const clearHoverProductHandleDebounce = () => {
    clearTimeout(debounceTimeout);
  };

  const isMultiCategory = activeNavCategorySet.productTags.length > 1;
  const visibleCollections = activeNavCategorySet.productTags
    .map((productTag) => {
      return allCollections[productTag] as Collection;
    })
    .filter(({ productTag }) => filteredProducts.some((p) => p.tags.includes(productTag)));
  const combineCategories = !!appliedFilters.sort[0] && isMultiCategory;
  const cardStyle: React.CSSProperties = isMobile ? {} : { flexDirection: 'row', paddingTop: '60px', paddingBottom: '60px' };

  const getBundlesForProductTag = (productTag: ProductTag) =>
    Object.keys(bundleConfiguration).filter((bundleConfigKey) =>
      bundleConfiguration[bundleConfigKey as BundleCollectionHandle].showForProductTags?.includes(productTag),
    ) as BundleCollectionHandle[];

  return (
    <>
      {combineCategories ? (
        <>
          <CardBody style={cardStyle}>
            {!hideHeader &&
              (hoverProduct && hoverVariant ? (
                <CollectionHoverHeader hoverProduct={hoverProduct} hoverVariant={hoverVariant} />
              ) : (
                <CollectionHeader title={`Sorted: ${appliedFilters.sort[0]}`} />
              ))}
            <div className={styles.collectionCards}>
              {filteredProducts.map((product, i) => (
                <CollectionCard
                  key={product.handle}
                  product={product}
                  setHoverProductHandle={setHoverProductHandleDebounce}
                  clearHoverProductHandleDebounce={clearHoverProductHandleDebounce}
                  selectionKey={`${i}-${product.handle}`}
                />
              ))}
            </div>
          </CardBody>
          <CardFooter></CardFooter>
        </>
      ) : (
        visibleCollections.map((collection, index) => (
          <React.Fragment key={index}>
            <CardBody style={cardStyle}>
              {!hideHeader &&
                (hoverVariant && hoverProduct?.tags.includes(collection?.productTag) ? (
                  <CollectionHoverHeader hoverProduct={hoverProduct} hoverVariant={hoverVariant} />
                ) : (
                  <CollectionHeader title={collection.title} description={collection.description}></CollectionHeader>
                ))}
              <div className={styles.collectionCards}>
                {filteredProducts.map(
                  (product, i) =>
                    product.tags.includes(collection?.productTag) && (
                      <CollectionCard
                        key={`${product.handle}${activeNavCategorySet.navCategory}`}
                        product={product}
                        setHoverProductHandle={setHoverProductHandleDebounce}
                        clearHoverProductHandleDebounce={clearHoverProductHandleDebounce}
                        selectionKey={`${i}-${product.handle}`}
                      />
                    ),
                )}
                {getBundlesForProductTag(collection.productTag).map((bundleProductHandle, i) => {
                  return (
                    <CollectionBundleCard key={i} collectionHandle={bundleProductHandle} clearHoverProductHandleDebounce={clearHoverProductHandleDebounce} />
                  );
                })}
              </div>
            </CardBody>
            <CardFooter></CardFooter>
            {index !== visibleCollections.length - 1 && <CardHeader></CardHeader>}
          </React.Fragment>
        ))
      )}
    </>
  );
};

const filterSize = (sizes: LongSize[]) => (product: ViewProduct) =>
  sizes.length === 0 || product.variants.some((variant) => variant.skuAvailability && sizes.includes(variant.size as LongSize));
const filterColor = (colors: Color[]) => (product: ViewProduct) =>
  colors.length === 0 || product.variants.some((variant) => variant.skuAvailability && colors.includes(variant.color));
const filterFeature = (features: ProductFeature[]) => (product: ViewProduct) =>
  features.length === 0 || (product.variants.some((variant) => variant.skuAvailability) && product.features.some((feature) => features.includes(feature)));

const sortProducts =
  (sortType: GarmentSort | undefined) =>
  (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
    switch (sortType) {
      case 'new':
        return sortByNewest(a, b);
      case 'price low-to-high':
        return sortByPriceUp(a, b);
      case 'price high-to-low':
        return sortByPriceDown(a, b);
      case 'popular':
      default:
        return sortByPopularity(a, b);
    }
  };

const sortByNewest = (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
  const aDate = new Date(a.published_at);
  const bDate = new Date(b.published_at);
  return aDate === bDate ? 0 : aDate < bDate ? 1 : -1;
};

export const sortByPopularity = (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
  const aPop = getPopularity(a.handle);
  const bPop = getPopularity(b.handle);
  return aPop === bPop ? 0 : aPop > bPop ? 1 : -1;
};
const sortByPriceUp = (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
  return a.price === b.price ? 0 : a.price > b.price ? 1 : -1;
};

const sortByPriceDown = (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
  return a.price === b.price ? 0 : a.price < b.price ? 1 : -1;
};

export const sortByProductFirst =
  (firstProduct: ViewProduct) =>
  (a: ViewProduct, b: ViewProduct): -1 | 0 | 1 => {
    switch (true) {
      case a.id === firstProduct.id && b.id !== firstProduct.id:
        return -1;
      case a.id !== firstProduct.id && b.id === firstProduct.id:
        return 1;
      default:
        return 0;
    }
  };

export default CollectionCards;
